import './claim-entity.detail.contact.scss';
import './claim-entity.detail.claimant.modal.scss';

import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  CreateClaimantField,
  UNITED_STATES_OF_AMERICA_LABEL,
  getCreateClaimantFieldProps,
} from '@converge/data';
import { Control, FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  ConvergeCheckbox,
  NewCountryFormSelectField,
  NewFormTextField,
  NewRegionFormSelectField,
} from '../../../presentation';

import { ApolloError } from '@apollo/client';
import CloseIcon from '@mui/icons-material/Close';
import { FC, useEffect, useMemo, useRef } from 'react';
// import { DialogLayoutHr } from '@converge/ui/presentation';
import { NewConvergeButton } from '../../../presentation/components/new-button';
import { PhoneExtField } from '../../../presentation/components/phone-ext-field';

import { useConvergeSnackBar } from '../../../contexts';
import {
  useUpsertClaimContactsMutation,
  ContactType,
  ClaimantType,
} from '@converge/graphql';

// Define proper interfaces for the data structures

interface initalContactData {
  type: ContactType;
  value: string;
  uuid?: string;
  contactUuid?: string;
}

export interface initalAddressData {
  street1: string;
  street2?: string;
  city: string;
  regionUuid: string;
  postalCode: string;
  countryUuid: string;
  uuid?: string;
  addressUuid?: string;
}

export interface ClaimantInitialData {
  uuid?: string;
  claimantUuid?: string;
  firstName?: string | null;
  lastName?: string | null;
  companyName?: string | null;
  contacts?: initalContactData[];
  addresses?: initalAddressData[];
  isInsured?: boolean;
}

export interface FormData {
  firstName: string | null;
  lastName: string | null;
  companyName: string | null;
  email: string;
  primaryPhoneNumber: string;
  primaryPhoneNumberExtn: string;
  address1: string;
  address2: string;
  city: string;
  regionUuid: string;
  postalCode: string;
  countryUuid: string;
  isInsured: boolean;
}

export interface InsuredData {
  companyName?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  address?: {
    street1?: string;
    street2?: string;
    city?: string;
    regionUuid?: string;
    postalCode?: string;
    countryUuid?: string;
  };
}

interface ClaimEntityClaimantModalProps {
  open: boolean;
  titleName: string;
  type: ClaimantType | undefined;
  claimUuid: string;
  editMode?: boolean;
  initialData?: ClaimantInitialData; // Data for editing
  insuredData?: InsuredData;
  onClose: () => void;
  onNoteSaved?: () => void;
  onSave?: (
    vendors: Array<{
      uuid: string;
      name: string;
      typeName: string;
      typeUuid: string;
    }>
  ) => void;
}

const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const validationSchema = yup.object().shape({
  [CreateClaimantField.Email]: yup
    .string()
    .matches(emailRegex, 'Please enter a valid email address')
    .nullable()
    .transform((value) => (value === '' ? null : value)),
  [CreateClaimantField.Address1]: yup
    .string()
    .required('This is required')
    .trim()
    .min(1, 'This is required'),
  [CreateClaimantField.City]: yup
    .string()
    .required('This is required')
    .trim()
    .min(1, 'This is required'),
  [CreateClaimantField.CountryUuid]: yup
    .string()
    .required('This is required')
    .min(1, 'This is required'),
  [CreateClaimantField.RegionUuid]: yup
    .string()
    .required('This is required')
    .min(1, 'This is required'),
  [CreateClaimantField.PostalCode]: yup
    .string()
    .required('This is required')
    .trim()
    .min(1, 'This is required'),
});
const validationSchemaForAdditionalContact = yup.object().shape({
  [CreateClaimantField.Email]: yup
    .string()
    .matches(emailRegex, 'Please enter a valid email address')
    .nullable()
    .transform((value) => (value === '' ? null : value)),
});

export const ClaimEntityClaimantModal: FC<ClaimEntityClaimantModalProps> = ({
  open,
  onClose,
  titleName,
  onSave,
  onNoteSaved,
  type,
  claimUuid,
  editMode,
  initialData,
  insuredData,
}) => {
  const resolver = useMemo(() => {
    return yupResolver(
      type !== ClaimantType.ClaimContact
        ? validationSchema
        : validationSchemaForAdditionalContact
    );
  }, [type]);

  const formMethods = useForm({
    resolver,
    defaultValues: {
      [CreateClaimantField.FirstName]: initialData?.firstName || '',
      [CreateClaimantField.LastName]: initialData?.lastName || '',
      [CreateClaimantField.CompanyName]: initialData?.companyName || '',
      [CreateClaimantField.Email]:
        initialData?.contacts?.find(
          (c: initalContactData) => c.type === ContactType.Email
        )?.value || '',
      [CreateClaimantField.PrimaryPhoneNumber]:
        initialData?.contacts
          ?.find((c: initalContactData) => c.type === ContactType.Phone)
          ?.value?.split('x')[0] || '',
      [CreateClaimantField.PrimaryPhoneNumberExtn]:
        initialData?.contacts
          ?.find((c: initalContactData) => c.type === ContactType.Phone)
          ?.value?.split('x')[1] || '',
      [CreateClaimantField.Address1]:
        initialData?.addresses?.[0]?.street1 || '',
      [CreateClaimantField.Address2]:
        initialData?.addresses?.[0]?.street2 || '',
      [CreateClaimantField.City]: initialData?.addresses?.[0]?.city || '',
      [CreateClaimantField.RegionUuid]:
        initialData?.addresses?.[0]?.regionUuid || '',
      [CreateClaimantField.PostalCode]:
        initialData?.addresses?.[0]?.postalCode || '',
      [CreateClaimantField.CountryUuid]:
        initialData?.addresses?.[0]?.countryUuid ||
        UNITED_STATES_OF_AMERICA_LABEL,
      // needed to be updated
      [CreateClaimantField.isInsured]: initialData?.isInsured || false,
    },

    mode: 'onChange', // Validate on change for better user experience
  });
  // Extract form states for validation
  const {
    formState: { errors },
    handleSubmit,
    reset,
    watch,
    setValue,
  } = formMethods;

  // Watch all form fields to determine if save button should be enabled
  const watchedFields = watch();

  // Get the default country value from form defaults (matches what's set in defaultValues)
  const defaultCountryValue =
    initialData?.addresses?.[0]?.countryUuid || UNITED_STATES_OF_AMERICA_LABEL;

  // Check if any non-country field has content
  // For new claimants (no initialData), only consider non-country fields
  // For editing existing claimants, also consider country changes
  const isValid = Object.entries(watchedFields).some(([key, value]) => {
    if (key === CreateClaimantField.CountryUuid) {
      // Only enable for country changes if we're editing an existing claimant
      if (editMode && initialData) {
        return value !== defaultCountryValue;
      }
      // For new claimants, ignore country field
      return false;
    }
    return typeof value === 'string' && value.trim().length > 0;
  });
  const { notify } = useConvergeSnackBar();
  const { upsertClaimContacts, loading } = useUpsertClaimContactsMutation();

  const isInsuredChecked = watch(CreateClaimantField.isInsured);
  const previousIsInsuredChecked = useRef<boolean | undefined>(undefined);

  useEffect(() => {
    if (initialData && editMode) {
      reset({
        [CreateClaimantField.FirstName]: initialData?.firstName || '',
        [CreateClaimantField.LastName]: initialData?.lastName || '',
        [CreateClaimantField.CompanyName]: initialData?.companyName || '',
        [CreateClaimantField.Email]:
          initialData?.contacts?.find(
            (c: initalContactData) => c.type === ContactType.Email
          )?.value || '',
        [CreateClaimantField.PrimaryPhoneNumber]:
          initialData?.contacts
            ?.find((c: initalContactData) => c.type === ContactType.Phone)
            ?.value?.split('x')[0] || '',
        [CreateClaimantField.PrimaryPhoneNumberExtn]:
          initialData?.contacts
            ?.find((c: initalContactData) => c.type === ContactType.Phone)
            ?.value?.split('x')[1] || '',
        [CreateClaimantField.Address1]:
          initialData?.addresses?.[0]?.street1 || '',
        [CreateClaimantField.Address2]:
          initialData?.addresses?.[0]?.street2 || '',
        [CreateClaimantField.City]: initialData?.addresses?.[0]?.city || '',
        [CreateClaimantField.RegionUuid]:
          initialData?.addresses?.[0]?.regionUuid || '',
        [CreateClaimantField.PostalCode]:
          initialData?.addresses?.[0]?.postalCode || '',
        [CreateClaimantField.CountryUuid]:
          initialData?.addresses?.[0]?.countryUuid ||
          UNITED_STATES_OF_AMERICA_LABEL,
        [CreateClaimantField.isInsured]: initialData?.isInsured || false,
      });
    }
  }, [initialData, editMode, reset]);

  // Separate useEffect for handling checkbox changes
  useEffect(() => {
    const prevChecked = previousIsInsuredChecked.current;

    // Only run when checkbox state actually changes, not on initial form load
    if (prevChecked === isInsuredChecked) {
      return;
    }

    // Update the previous value
    previousIsInsuredChecked.current = isInsuredChecked;

    if (isInsuredChecked && insuredData) {
      setValue(CreateClaimantField.CompanyName, insuredData.companyName || '');
      setValue(CreateClaimantField.FirstName, insuredData.firstName || '');
      setValue(CreateClaimantField.LastName, insuredData.lastName || '');
      setValue(CreateClaimantField.Email, insuredData.email || '');
      const phoneNumber = insuredData.phone || '';
      const [mainPhone, ext] = phoneNumber.split('x');
      setValue(CreateClaimantField.PrimaryPhoneNumber, mainPhone || '');
      setValue(CreateClaimantField.PrimaryPhoneNumberExtn, ext || '');
      setValue(
        CreateClaimantField.Address1,
        insuredData.address?.street1 || ''
      );
      setValue(
        CreateClaimantField.Address2,
        insuredData.address?.street2 || ''
      );
      setValue(CreateClaimantField.City, insuredData.address?.city || '');
      setValue(
        CreateClaimantField.RegionUuid,
        insuredData.address?.regionUuid || ''
      );
      setValue(
        CreateClaimantField.PostalCode,
        insuredData.address?.postalCode || ''
      );
      setValue(
        CreateClaimantField.CountryUuid,
        insuredData.address?.countryUuid || UNITED_STATES_OF_AMERICA_LABEL
      );
    } else if (!isInsuredChecked && prevChecked === true) {
      // Only clear when user explicitly unchecked the checkbox (true → false).
      // When prevChecked is undefined the modal just opened; clearing would wipe prefilled edit data.
      setValue(CreateClaimantField.CompanyName, '');
      setValue(CreateClaimantField.FirstName, '');
      setValue(CreateClaimantField.LastName, '');
      setValue(CreateClaimantField.Email, '');
      setValue(CreateClaimantField.PrimaryPhoneNumber, '');
      setValue(CreateClaimantField.PrimaryPhoneNumberExtn, '');
      setValue(CreateClaimantField.Address1, '');
      setValue(CreateClaimantField.Address2, '');
      setValue(CreateClaimantField.City, '');
      setValue(CreateClaimantField.RegionUuid, '');
      setValue(CreateClaimantField.PostalCode, '');
      setValue(CreateClaimantField.CountryUuid, UNITED_STATES_OF_AMERICA_LABEL);
    }
  }, [isInsuredChecked, insuredData, setValue]);

  // Reset previous checkbox value when modal opens
  useEffect(() => {
    if (open) {
      previousIsInsuredChecked.current = undefined;
    }
  }, [open]);
  // Handle form cancel or close
  const handleCancel = () => {
    reset({
      [CreateClaimantField.FirstName]: '',
      [CreateClaimantField.LastName]: '',
      [CreateClaimantField.CompanyName]: '',
      [CreateClaimantField.Email]: '',
      [CreateClaimantField.PrimaryPhoneNumber]: '',
      [CreateClaimantField.PrimaryPhoneNumberExtn]: '',
      [CreateClaimantField.Address1]: '',
      [CreateClaimantField.Address2]: '',
      [CreateClaimantField.City]: '',
      [CreateClaimantField.RegionUuid]: '',
      [CreateClaimantField.PostalCode]: '',
      [CreateClaimantField.CountryUuid]: UNITED_STATES_OF_AMERICA_LABEL,
      [CreateClaimantField.isInsured]: false,
    });
    onClose();
  };

  // Helper function to format phone number properly
  const formatPhoneNumber = (
    phoneNumber: string,
    extension: string
  ): string => {
    if (!phoneNumber) return '';
    if (!extension || extension.trim() === '') return phoneNumber;
    return `${phoneNumber}x${extension}`;
  };

  // Helper function to get only changed fields
  const getChangedFields = (newData: FormData) => {
    if (!editMode || !initialData) {
      // If not in edit mode, return all fields
      const formattedPhone = formatPhoneNumber(
        newData.primaryPhoneNumber || '',
        newData.primaryPhoneNumberExtn || ''
      );

      return {
        firstName:
          newData.firstName && newData.firstName.trim().length > 0
            ? newData.firstName
            : null,
        lastName:
          newData.lastName && newData.lastName.trim().length > 0
            ? newData.lastName
            : null,
        type: type,
        companyName:
          newData.companyName && newData.companyName.trim().length > 0
            ? newData.companyName
            : null,
        contacts: [
          {
            type: ContactType.Email,
            value: newData.email || '',
          },
          {
            type: ContactType.Phone,
            value: formattedPhone,
          },
        ],
        addresses: [
          {
            street1: newData.address1,
            street2: newData.address2,
            city: newData.city,
            regionUuid: newData.regionUuid,
            postalCode: newData.postalCode,
            countryUuid: newData.countryUuid,
          },
        ],
        isInsured: newData.isInsured,
      };
    }

    const changes: Partial<ClaimantInitialData & { type: ClaimantType }> = {};

    // Always add personUuid and type for editing (required for edit operations)
    if (initialData.uuid) {
      changes.claimantUuid = initialData.uuid;
      changes.type = type;
    }

    // Check basic fields
    if (newData.firstName !== initialData.firstName) {
      changes.firstName =
        newData.firstName && newData.firstName.trim().length > 0
          ? newData.firstName
          : null;
    }
    if (newData.lastName !== initialData.lastName) {
      changes.lastName =
        newData.lastName && newData.lastName.trim().length > 0
          ? newData.lastName
          : null;
    }
    if (newData.companyName !== initialData.companyName) {
      changes.companyName =
        newData.companyName && newData.companyName.trim().length > 0
          ? newData.companyName
          : null;
    }

    // Check contacts
    const initialEmail =
      initialData.contacts?.find(
        (c: initalContactData) => c.type === ContactType.Email
      )?.value || '';
    const initialPhone =
      initialData.contacts?.find(
        (c: initalContactData) => c.type === ContactType.Phone
      )?.value || '';

    const newPhone = formatPhoneNumber(
      newData.primaryPhoneNumber || '',
      newData.primaryPhoneNumberExtn || ''
    );

    const contactChanges: initalContactData[] = [];
    if (newData.email !== initialEmail) {
      contactChanges.push({
        type: ContactType.Email,
        value: newData.email || '',
        contactUuid: initialData?.contacts?.find(
          (contact) => contact.type === ContactType.Email
        )?.uuid,
      });
    }
    if (newPhone !== initialPhone) {
      contactChanges.push({
        type: ContactType.Phone,
        value: newPhone,
        contactUuid: initialData?.contacts?.find(
          (contact) => contact.type === ContactType.Phone
        )?.uuid,
      });
    }
    if (contactChanges.length > 0) {
      changes.contacts = contactChanges;
    }

    // Check address
    const initialAddress: initalAddressData = initialData.addresses?.[0] || {
      street1: '',
      street2: '',
      city: '',
      regionUuid: '',
      postalCode: '',
      countryUuid: '',
      uuid: '',
    };
    const addressChanged =
      newData.address1 !== initialAddress.street1 ||
      newData.address2 !== (initialAddress.street2 || '') ||
      newData.city !== initialAddress.city ||
      newData.regionUuid !== initialAddress.regionUuid ||
      newData.postalCode !== initialAddress.postalCode ||
      newData.countryUuid !== initialAddress.countryUuid;

    if (addressChanged) {
      changes.addresses = [
        {
          street1: newData.address1,
          street2: newData.address2,
          city: newData.city,
          regionUuid: newData.regionUuid,
          postalCode: newData.postalCode,
          countryUuid: newData.countryUuid,
          addressUuid: initialAddress.uuid,
        },
      ];
    }

    // Check isInsured
    if (newData.isInsured !== initialData.isInsured) {
      changes.isInsured = newData.isInsured;
    }

    // Ensure personUuid and type are always included in edit mode, even if no other changes
    if (editMode && initialData.uuid) {
      if (!changes.claimantUuid) {
        changes.claimantUuid = initialData.uuid;
      }
      if (!changes.type) {
        changes.type = type;
      }
    }

    return changes;
  };

  // Handle form submission
  const onSubmit = handleSubmit(async (data) => {
    const details = getChangedFields(data);

    let responseErrors;
    try {
      await upsertClaimContacts(claimUuid, details);

      if (onSave) {
        onSave([
          {
            uuid: 'temp-uuid',
            name: `${data[CreateClaimantField.FirstName]} ${
              data[CreateClaimantField.LastName]
            }`,
            typeName: 'Claimant',
            typeUuid: 'claimant-type-uuid',
          },
        ]);
      }

      // Log successful creation/update
      notify(
        editMode
          ? 'Claimant successfully updated'
          : 'Claimant successfully added',
        {
          variant: 'success',
        }
      );

      // Trigger refresh if callback provided
      if (onNoteSaved) {
        onNoteSaved();
      }

      // Close the modal
      handleCancel();
    } catch (cause) {
      console.error(
        `Failed to ${
          editMode ? 'update' : 'add'
        } claimant. Please try again. (${cause})`
      );
      if (cause instanceof ApolloError) {
        responseErrors = cause.graphQLErrors;
      }

      const errorMessage = responseErrors?.[0]?.message;
      notify(
        `Failed to ${editMode ? 'update' : 'add'} claimant. Please try again: ${
          errorMessage || 'Unknown error'
        }`,
        { variant: 'error' }
      );
    }
  });

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth={false}
      fullWidth={true}
      PaperProps={{
        sx: {
          width: '895px',
          height: 'auto',

          margin: 'auto',
        },
      }}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={onSubmit}>
          <DialogTitle sx={{ padding: 4, paddingBottom: 5, marginBottom: 6 }}>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              paddingTop="10px"
              paddingRight="15px"
            >
              <Box className="main-title-claimant-modal" sx={{ marginTop: 2 }}>
                {titleName}
              </Box>
              <IconButton
                edge="end"
                color="inherit"
                onClick={handleCancel}
                aria-label="close"
              >
                <CloseIcon />
              </IconButton>
            </Stack>
          </DialogTitle>

          <DialogContent>
            <Box>
              <Stack
                direction="row"
                width="100%"
                spacing={5}
                sx={{ mb: 3 }}
                className="claimant-row-container"
              >
                <Box
                  sx={{
                    width: {
                      xs: '45%', // Less than 1600px
                      xl: '45.2%', // 1600px and above
                    },
                  }}
                >
                  <NewFormTextField
                    {...getCreateClaimantFieldProps(
                      CreateClaimantField.FirstName
                    )}
                    placeholder="Enter First Name"
                    error={!!errors[CreateClaimantField.FirstName]}
                    helperText={
                      (errors[CreateClaimantField.FirstName]
                        ?.message as string) || ''
                    }
                  />
                </Box>
                <Box
                  sx={{
                    width: {
                      xs: '45%', // Less than 1600px
                      xl: '45.2%', // 1600px and above
                    },
                  }}
                >
                  <NewFormTextField
                    {...getCreateClaimantFieldProps(
                      CreateClaimantField.LastName
                    )}
                    containerWidth={12}
                    placeholder="Enter Last Name"
                    error={!!errors[CreateClaimantField.LastName]}
                    helperText={
                      (errors[CreateClaimantField.LastName]
                        ?.message as string) || ''
                    }
                  />
                </Box>
              </Stack>

              <Stack
                direction="row"
                spacing={6}
                sx={{ mb: 3 }}
                className="claimant-row-container"
              >
                <Box width="30%">
                  <NewFormTextField
                    {...getCreateClaimantFieldProps(
                      CreateClaimantField.CompanyName
                    )}
                    containerWidth={12}
                    placeholder="Enter Company if Applicable"
                  />
                </Box>

                <Box width="30%">
                  <NewFormTextField
                    {...getCreateClaimantFieldProps(CreateClaimantField.Email)}
                    containerWidth={12}
                    placeholder="name@domain.com"
                    error={!!errors[CreateClaimantField.Email]}
                    helperText={
                      (errors[CreateClaimantField.Email]?.message as string) ||
                      ''
                    }
                  />
                </Box>
                <Box width="30%">
                  <PhoneExtField
                    isContactpage={true}
                    dataTestId="claimant-primaryPhone"
                    placeholder="415-555-1212"
                    className="phone-field"
                    mainClassName="primary-phone2"
                    autoComplete={false}
                    dataExtTestId="mobile-phone-extn"
                    classNameExt="mobile-phone-extn"
                    fieldPropsIdx={CreateClaimantField.PrimaryPhoneNumber}
                    fieldPropsExtIdx={
                      CreateClaimantField.PrimaryPhoneNumberExtn
                    }
                  />
                </Box>
              </Stack>
              <Stack
                direction="row"
                spacing={6}
                sx={{ mb: 3 }}
                className="claimant-row-container"
              >
                <Box width="30%">
                  <NewFormTextField
                    {...getCreateClaimantFieldProps(
                      CreateClaimantField.Address1
                    )}
                    containerWidth={12}
                    placeholder="1234 Main St"
                    required={type !== ClaimantType.ClaimContact}
                    error={!!errors[CreateClaimantField.Address1]}
                    helperText={
                      (errors[CreateClaimantField.Address1]
                        ?.message as string) || ''
                    }
                  />
                </Box>

                <Box width="30%">
                  <NewFormTextField
                    {...getCreateClaimantFieldProps(
                      CreateClaimantField.Address2
                    )}
                    containerWidth={12}
                    placeholder="Suite 123"
                  />
                </Box>
                <Box width="30%">
                  <NewFormTextField
                    {...getCreateClaimantFieldProps(CreateClaimantField.City)}
                    containerWidth={12}
                    placeholder="Enter City"
                    required={type !== ClaimantType.ClaimContact}
                    error={!!errors[CreateClaimantField.City]}
                    helperText={
                      (errors[CreateClaimantField.City]?.message as string) ||
                      ''
                    }
                  />
                </Box>
              </Stack>
              <Stack
                direction="row"
                spacing={6}
                sx={{ mb: 3 }}
                className="claimant-row-container"
              >
                <Box width="30%">
                  <NewCountryFormSelectField
                    {...getCreateClaimantFieldProps(
                      CreateClaimantField.CountryUuid
                    )}
                    data-testid="brokerage-primaryCountry"
                    required={type !== ClaimantType.ClaimContact}
                  />
                </Box>
                <Box width="30%">
                  <NewRegionFormSelectField
                    {...getCreateClaimantFieldProps(
                      CreateClaimantField.RegionUuid
                    )}
                    data-testid="brokerage-primaryState"
                    required={type !== ClaimantType.ClaimContact}
                  />
                </Box>
                <Box width="30%">
                  <NewFormTextField
                    {...getCreateClaimantFieldProps(
                      CreateClaimantField.PostalCode
                    )}
                    containerWidth={12}
                    placeholder="Enter postal code"
                    required={type !== ClaimantType.ClaimContact}
                  />
                </Box>
              </Stack>
              {type !== ClaimantType.ClaimContact && (
                <Stack
                  direction="row"
                  spacing={6}
                  sx={{ mb: 3 }}
                  className="claimant-row-container"
                >
                  <Box
                    width="30%"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mt: 2,
                    }}
                  >
                    <ConvergeCheckbox
                      name="isInsured"
                      control={
                        formMethods.control as unknown as Control<
                          Record<string, unknown>
                        >
                      }
                      dataTestId="isInsured"
                    />
                    <Typography className="claimant-checkbox">
                      Claimant same as Insured
                    </Typography>
                  </Box>
                  {/* <Box width="30%">
               
                </Box>
                <Box width="30%">
              
                </Box> */}
                </Stack>
              )}
            </Box>
            {/* Save Button */}
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'flex-end',
                width: '100%',
                mt: 'auto',
                py: 2.0,
                px: 0.3,
                marginBottom: '10px',
                backgroundColor: 'white',
              }}
            >
              <Box>
                <NewConvergeButton
                  sx={{ width: '100px', marginRight: '20px' }}
                  data-testid="claimant-cancel-btn"
                  variant="outlined"
                  label="cancel"
                  onClick={handleCancel}
                />
              </Box>
              <Box sx={{ width: '100px', marginRight: '20px' }}>
                <NewConvergeButton
                  data-testid="claimant-save-btn"
                  variant="contained"
                  label="SAVE"
                  type="submit"
                  loading={loading}
                  disabled={!isValid}
                  sx={{
                    bgcolor: '#8dd0c3',
                    '&:hover': {
                      bgcolor: '#7bc0b3',
                    },
                    '&.Mui-disabled': {
                      bgcolor: '#cccccc',
                      color: '#666666',
                    },
                  }}
                />
              </Box>
            </Box>
          </DialogContent>
        </form>
      </FormProvider>
    </Dialog>
  );
};
